/**
 * Wave 39: Remaining Central America + Africa + Pacific
 * Tegucigalpa, San Salvador, Managua, Khartoum, Tripoli, Ouagadougou, Niamey, Freetown, Monrovia, Suva
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development.' },
  { name: 'Web Application Development', description: 'Enterprise web applications and portals.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions and automation.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure and CI/CD.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping.' },
  { name: 'Cybersecurity', description: 'Security audits, compliance, penetration testing.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers integrated into your team.' },
];
const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Mobile money, payment platforms.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplaces.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine.' },
  { name: 'SaaS & Technology', description: 'Product development and scaling.' },
  { name: 'AgriTech', description: 'Agricultural technology platforms.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, optimization.' },
];
const makeFaqs = (city: string, country: string) => [
  { question: `Why ${city}?`, answer: `${city}, ${country} offers strategic positioning and competitive rates.` },
  { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
  { question: 'Quality?', answer: 'Code reviews, CI/CD, 80%+ test coverage.' },
  { question: 'Compliance?', answer: 'ISO 27001, GDPR-aligned, NDAs.' },
  { question: 'Start time?', answer: '2-week onboarding.' },
  { question: 'Models?', answer: 'T&M, fixed-price, dedicated teams.' },
];

export const CITY_AGENCY_PAGES_WAVE39: Record<string, CityAgencyData> = {
  tegucigalpa: {
    slug: 'software-development-agency-tegucigalpa', cityName: 'Tegucigalpa', countryName: 'Honduras', flag: '🇭🇳',
    timezone: 'CST (UTC-6)', currency: 'HNL',
    title: { en: 'Software Development Agency Tegucigalpa | Call IT Dev', fr: 'Agence Développement Informatique Tegucigalpa | Call IT Dev' },
    description: { en: 'Software development for Tegucigalpa, Honduras. Central America, CST timezone.', fr: 'Agence de développement pour Tegucigalpa, Honduras.' },
    keywords: ['software development Tegucigalpa', 'IT outsourcing Honduras'],
    heroTitle: { en: 'Software Development Agency for Tegucigalpa', fr: 'Agence de Développement pour Tegucigalpa' },
    heroSubtitle: { en: 'Honduras capital with growing nearshore BPO sector and US-aligned timezone.', fr: 'Capitale du Honduras avec secteur BPO nearshore croissant.' },
    whyTitle: { en: 'Why Tegucigalpa', fr: 'Pourquoi Tegucigalpa' },
    whyReasons: [
      { icon: '⏰', title: 'CST Timezone', description: 'US Central Time alignment.' },
      { icon: '💰', title: '65% Savings', description: 'Competitive rates.' },
      { icon: '🗣️', title: 'Spanish Speaking', description: 'Native Spanish teams.' },
      { icon: '📈', title: 'BPO Growth', description: 'Honduras growing as nearshore hub.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Tegucigalpa', 'Honduras'),
    localRate: '$20-35', ourRate: '$20-35', savings: '45%+',
  },
  sanSalvador: {
    slug: 'software-development-agency-san-salvador', cityName: 'San Salvador', countryName: 'El Salvador', flag: '🇸🇻',
    timezone: 'CST (UTC-6)', currency: 'USD',
    title: { en: 'Software Development Agency San Salvador | Call IT Dev', fr: 'Agence Développement Informatique San Salvador | Call IT Dev' },
    description: { en: 'Software development for San Salvador, El Salvador. USD economy, Bitcoin pioneer.', fr: 'Agence de développement pour San Salvador, El Salvador. Économie USD.' },
    keywords: ['software development San Salvador', 'IT outsourcing El Salvador', 'bitcoin development'],
    heroTitle: { en: 'Software Development Agency for San Salvador', fr: 'Agence de Développement pour San Salvador' },
    heroSubtitle: { en: 'El Salvador\'s capital — Bitcoin pioneer with USD economy and US-aligned timezone.', fr: 'Capitale du Salvador — pionnier Bitcoin avec économie USD.' },
    whyTitle: { en: 'Why San Salvador', fr: 'Pourquoi San Salvador' },
    whyReasons: [
      { icon: '₿', title: 'Bitcoin Pioneer', description: 'First country to adopt Bitcoin as legal tender.' },
      { icon: '💵', title: 'USD Economy', description: 'No FX risk — USD is the currency.' },
      { icon: '⏰', title: 'CST Timezone', description: 'US Central Time alignment.' },
      { icon: '💰', title: '60% Savings', description: 'Competitive nearshore rates.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('San Salvador', 'El Salvador'),
    localRate: '$25-40', ourRate: '$20-35', savings: '50%+',
  },
  managua: {
    slug: 'software-development-agency-managua', cityName: 'Managua', countryName: 'Nicaragua', flag: '🇳🇮',
    timezone: 'CST (UTC-6)', currency: 'NIO',
    title: { en: 'Software Development Agency Managua | Call IT Dev', fr: 'Agence Développement Informatique Managua | Call IT Dev' },
    description: { en: 'Software development for Managua, Nicaragua. Central America, CST timezone.', fr: 'Agence de développement pour Managua, Nicaragua.' },
    keywords: ['software development Managua', 'IT outsourcing Nicaragua'],
    heroTitle: { en: 'Software Development Agency for Managua', fr: 'Agence de Développement pour Managua' },
    heroSubtitle: { en: 'Nicaragua\'s capital with competitive labor costs and growing tech sector.', fr: 'Capitale du Nicaragua avec coûts compétitifs et secteur tech croissant.' },
    whyTitle: { en: 'Why Managua', fr: 'Pourquoi Managua' },
    whyReasons: [
      { icon: '💰', title: 'Low Costs', description: 'Among the most competitive in Central America.' },
      { icon: '⏰', title: 'CST Timezone', description: 'US Central Time alignment.' },
      { icon: '🗣️', title: 'Spanish Speaking', description: 'Native Spanish teams.' },
      { icon: '📈', title: 'Free Zones', description: 'Special economic zones for tech companies.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Managua', 'Nicaragua'),
    localRate: '$18-35', ourRate: '$20-35', savings: '45%+',
  },
  khartoum: {
    slug: 'software-development-agency-khartoum', cityName: 'Khartoum', countryName: 'Sudan', flag: '🇸🇩',
    timezone: 'CAT (UTC+2)', currency: 'SDG',
    title: { en: 'Software Development Agency Khartoum | Call IT Dev', fr: 'Agence Développement Informatique Khartoum | Call IT Dev' },
    description: { en: 'Software development for Khartoum, Sudan. Arabic-speaking, strategic Nile position.', fr: 'Agence de développement pour Khartoum, Soudan. Arabophone, position stratégique.' },
    keywords: ['software development Khartoum', 'IT outsourcing Sudan'],
    heroTitle: { en: 'Software Development Agency for Khartoum', fr: 'Agence de Développement pour Khartoum' },
    heroSubtitle: { en: 'Sudan\'s capital at the confluence of the Nile. Arabic-speaking tech talent.', fr: 'Capitale du Soudan au confluent du Nil. Talents tech arabophones.' },
    whyTitle: { en: 'Why Khartoum', fr: 'Pourquoi Khartoum' },
    whyReasons: [
      { icon: '🗣️', title: 'Arabic Speaking', description: 'Native Arabic-speaking teams.' },
      { icon: '💰', title: '70% Savings', description: 'Ultra-competitive rates.' },
      { icon: '⏰', title: 'CAT = EET', description: 'Close to European timezones.' },
      { icon: '🌍', title: 'Nile Hub', description: 'Strategic East Africa position.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Khartoum', 'Sudan'),
    localRate: '$15-30', ourRate: '$20-35', savings: '50%+',
  },
  tripoli: {
    slug: 'software-development-agency-tripoli', cityName: 'Tripoli', countryName: 'Libya', flag: '🇱🇾',
    timezone: 'EET (UTC+2)', currency: 'LYD',
    title: { en: 'Software Development Agency Tripoli | Call IT Dev', fr: 'Agence Développement Informatique Tripoli | Call IT Dev' },
    description: { en: 'Software development for Tripoli, Libya. North African capital, Arabic-speaking.', fr: 'Agence de développement pour Tripoli, Libye. Capitale nord-africaine, arabophone.' },
    keywords: ['software development Tripoli', 'IT outsourcing Libya'],
    heroTitle: { en: 'Software Development Agency for Tripoli', fr: 'Agence de Développement pour Tripoli' },
    heroSubtitle: { en: 'Libya\'s capital rebuilding its digital infrastructure with Arabic-speaking teams.', fr: 'Capitale de Libye reconstruisant son infrastructure numérique.' },
    whyTitle: { en: 'Why Tripoli', fr: 'Pourquoi Tripoli' },
    whyReasons: [
      { icon: '🗣️', title: 'Arabic Speaking', description: 'Native Arabic teams.' },
      { icon: '💰', title: '65% Savings', description: 'Competitive rates.' },
      { icon: '🛢️', title: 'Oil Economy', description: 'Oil sector driving digital modernization.' },
      { icon: '⏰', title: 'EET', description: 'European timezone alignment.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Tripoli', 'Libya'),
    localRate: '$20-40', ourRate: '$20-35', savings: '50%+',
  },
  ouagadougou: {
    slug: 'software-development-agency-ouagadougou', cityName: 'Ouagadougou', countryName: 'Burkina Faso', flag: '🇧🇫',
    timezone: 'GMT (UTC+0)', currency: 'XOF',
    title: { en: 'Software Development Agency Ouagadougou | Call IT Dev', fr: 'Agence Développement Informatique Ouagadougou | Call IT Dev' },
    description: { en: 'Software development for Ouagadougou, Burkina Faso. Francophone West Africa.', fr: 'Agence de développement pour Ouagadougou, Burkina Faso.' },
    keywords: ['software development Ouagadougou', 'IT outsourcing Burkina Faso'],
    heroTitle: { en: 'Software Development Agency for Ouagadougou', fr: 'Agence de Développement pour Ouagadougou' },
    heroSubtitle: { en: 'Burkina Faso\'s capital with growing francophone tech talent pool.', fr: 'Capitale du Burkina Faso avec vivier de talents tech francophones.' },
    whyTitle: { en: 'Why Ouagadougou', fr: 'Pourquoi Ouagadougou' },
    whyReasons: [
      { icon: '🗣️', title: 'Francophone', description: 'French-speaking teams.' },
      { icon: '💰', title: '70% Savings', description: 'Ultra-competitive rates.' },
      { icon: '⏰', title: 'GMT', description: 'UK-aligned timezone.' },
      { icon: '📱', title: 'Mobile Innovation', description: 'Mobile-first digital economy.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Ouagadougou', 'Burkina Faso'),
    localRate: '$15-28', ourRate: '$20-35', savings: '45%+',
  },
  niamey: {
    slug: 'software-development-agency-niamey', cityName: 'Niamey', countryName: 'Niger', flag: '🇳🇪',
    timezone: 'WAT (UTC+1)', currency: 'XOF',
    title: { en: 'Software Development Agency Niamey | Call IT Dev', fr: 'Agence Développement Informatique Niamey | Call IT Dev' },
    description: { en: 'Software development for Niamey, Niger. Francophone Sahel capital.', fr: 'Agence de développement pour Niamey, Niger. Capitale sahélienne francophone.' },
    keywords: ['software development Niamey', 'IT outsourcing Niger'],
    heroTitle: { en: 'Software Development Agency for Niamey', fr: 'Agence de Développement pour Niamey' },
    heroSubtitle: { en: 'Niger\'s capital with emerging digital infrastructure.', fr: 'Capitale du Niger avec infrastructure numérique émergente.' },
    whyTitle: { en: 'Why Niamey', fr: 'Pourquoi Niamey' },
    whyReasons: [
      { icon: '🗣️', title: 'Francophone', description: 'French-speaking teams.' },
      { icon: '💰', title: '70% Savings', description: 'Ultra-competitive rates.' },
      { icon: '⏰', title: 'WAT = CET', description: 'European timezone alignment.' },
      { icon: '🌍', title: 'Sahel Hub', description: 'Strategic Sahel position.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Niamey', 'Niger'),
    localRate: '$15-28', ourRate: '$20-35', savings: '45%+',
  },
  freetown: {
    slug: 'software-development-agency-freetown', cityName: 'Freetown', countryName: 'Sierra Leone', flag: '🇸🇱',
    timezone: 'GMT (UTC+0)', currency: 'SLL',
    title: { en: 'Software Development Agency Freetown | Call IT Dev', fr: 'Agence Développement Informatique Freetown | Call IT Dev' },
    description: { en: 'Software development for Freetown, Sierra Leone. English-speaking West Africa.', fr: 'Agence de développement pour Freetown, Sierra Leone. Afrique de l\'Ouest anglophone.' },
    keywords: ['software development Freetown', 'IT outsourcing Sierra Leone'],
    heroTitle: { en: 'Software Development Agency for Freetown', fr: 'Agence de Développement pour Freetown' },
    heroSubtitle: { en: 'Sierra Leone\'s English-speaking capital with emerging digital economy.', fr: 'Capitale anglophone de Sierra Leone avec économie numérique émergente.' },
    whyTitle: { en: 'Why Freetown', fr: 'Pourquoi Freetown' },
    whyReasons: [
      { icon: '🗣️', title: 'English Speaking', description: 'English is the official language.' },
      { icon: '💰', title: '70% Savings', description: 'Ultra-competitive rates.' },
      { icon: '⏰', title: 'GMT', description: 'UK-aligned timezone.' },
      { icon: '📈', title: 'Digital Push', description: 'Government-backed digital transformation.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Freetown', 'Sierra Leone'),
    localRate: '$15-28', ourRate: '$20-35', savings: '50%+',
  },
  monrovia: {
    slug: 'software-development-agency-monrovia', cityName: 'Monrovia', countryName: 'Liberia', flag: '🇱🇷',
    timezone: 'GMT (UTC+0)', currency: 'LRD/USD',
    title: { en: 'Software Development Agency Monrovia | Call IT Dev', fr: 'Agence Développement Informatique Monrovia | Call IT Dev' },
    description: { en: 'Software development for Monrovia, Liberia. English-speaking, USD-linked economy.', fr: 'Agence de développement pour Monrovia, Libéria. Anglophone, économie liée au USD.' },
    keywords: ['software development Monrovia', 'IT outsourcing Liberia'],
    heroTitle: { en: 'Software Development Agency for Monrovia', fr: 'Agence de Développement pour Monrovia' },
    heroSubtitle: { en: 'Liberia\'s English-speaking capital with USD-linked economy.', fr: 'Capitale anglophone du Libéria avec économie liée au dollar.' },
    whyTitle: { en: 'Why Monrovia', fr: 'Pourquoi Monrovia' },
    whyReasons: [
      { icon: '🗣️', title: 'English Speaking', description: 'English is the official language.' },
      { icon: '💵', title: 'USD-Linked', description: 'Economy linked to US dollar.' },
      { icon: '💰', title: '70% Savings', description: 'Ultra-competitive rates.' },
      { icon: '⏰', title: 'GMT', description: 'UK-aligned timezone.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Monrovia', 'Liberia'),
    localRate: '$15-28', ourRate: '$20-35', savings: '50%+',
  },
  suva: {
    slug: 'software-development-agency-suva', cityName: 'Suva', countryName: 'Fiji', flag: '🇫🇯',
    timezone: 'FJT (UTC+12)', currency: 'FJD',
    title: { en: 'Software Development Agency Suva | Call IT Dev', fr: 'Agence Développement Informatique Suva | Call IT Dev' },
    description: { en: 'Software development for Suva, Fiji. Pacific Island hub, English-speaking.', fr: 'Agence de développement pour Suva, Fidji. Hub du Pacifique, anglophone.' },
    keywords: ['software development Suva', 'IT outsourcing Fiji', 'Pacific development'],
    heroTitle: { en: 'Software Development Agency for Suva', fr: 'Agence de Développement pour Suva' },
    heroSubtitle: { en: 'Pacific Island hub. English-speaking with growing digital economy and tourism tech.', fr: 'Hub du Pacifique. Anglophone avec économie numérique et tech touristique.' },
    whyTitle: { en: 'Why Suva', fr: 'Pourquoi Suva' },
    whyReasons: [
      { icon: '🌊', title: 'Pacific Hub', description: 'Fiji is the Pacific Islands\' economic center.' },
      { icon: '🗣️', title: 'English Speaking', description: 'English is an official language.' },
      { icon: '💰', title: '55% Savings', description: 'Competitive rates for Pacific enterprises.' },
      { icon: '🌴', title: 'Tourism Tech', description: 'Tourism drives digital innovation in Fiji.' },
    ],
    services: sharedServices, industries: sharedIndustries, faqs: makeFaqs('Suva', 'Fiji'),
    localRate: '$30-50', ourRate: '$20-35', savings: '50%+',
  },
};
